import {
  apiGetBoardModerationQueue,
  apiUpdateBoardPost,
  apiUploadBoardAttachment,
  apiRejectBoardPost,
  apiPublishBoardPost,
  apiGetBoardTargetUsers,
  apiListShiftChangeRequests,
  apiApproveShiftChangeRequest,
  apiRejectShiftChangeRequest,
} from '../api.js';
import { badgeClassForSlotCategory } from '../types.js';

function escapeHtml(text) {
  return String(text || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

function escapeAttr(text) {
  return escapeHtml(text).replaceAll('"', '&quot;');
}

function toDateTimeLocalValue(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw.slice(0, 16);
}

function formatDateItFull(value) {
  const s = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return value || '-';
  return `${s.slice(8, 10)}-${s.slice(5, 7)}-${s.slice(0, 4)}`;
}

function formatDateTimeIt(value) {
  const raw = String(value || '').trim();
  if (!raw) return '-';
  const normalized = raw.replace(' ', 'T');
  const m = normalized.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return raw;
  const [, y, mo, d, hh, mm, ss] = m;
  return `${d}-${mo}-${y}, ${hh}:${mm}:${ss || '00'}`;
}

function timeSlotShort(value) {
  const k = String(value || '').trim().toLowerCase();
  if (k === 'mattino') return 'M';
  if (k === 'pomeriggio') return 'P';
  if (k === 'prolungato') return 'PR';
  if (k === 'notte') return 'N';
  if (k === 'reperibilita_giorno') return 'RG';
  if (k === 'reperibilita_notte') return 'RN';
  return (k || '-').toUpperCase();
}

function shiftBadgeHtml({ name, category, timeSlot, date }) {
  const cls = badgeClassForSlotCategory(category || '');
  const slotName = String(name || '').trim() || String(category || '-').trim();
  const ts = timeSlotShort(timeSlot);
  const d = formatDateItFull(date);
  return `<span class="badge badge-${cls}">${escapeHtml(slotName)} ${escapeHtml(ts)} · ${escapeHtml(d)}</span>`;
}

function personBadgeLabel(username, sigla) {
  const s = String(sigla || '').trim().toUpperCase();
  if (s) return s;
  const u = String(username || '').trim().toLowerCase();
  return u || '-';
}

function targetUserLabel(user) {
  const sigla = String(user?.sigla || '').trim();
  const nome = String(user?.nome || '').trim();
  const cognome = String(user?.cognome || '').trim();
  const parts = [sigla, nome, cognome].filter(Boolean);
  if (parts.length) return parts.join(' ');
  return String(user?.username || user?.id || '').trim() || '-';
}

function shiftRequestStatusLabel(status) {
  const s = String(status || '').trim().toLowerCase();
  if (s === 'pending_peer') return 'In attesa collega';
  if (s === 'pending') return 'In attesa Direttore';
  if (s === 'approved') return 'Approvata';
  if (s === 'rejected') return 'Rifiutata dal Direttore';
  if (s === 'rejected_by_peer') return 'Rifiutata dal collega';
  return status || '-';
}

function shiftRequestStatusClass(status) {
  const s = String(status || '').trim().toLowerCase();
  if (s === 'pending_peer') return 'pending-peer';
  if (s === 'pending') return 'pending-director';
  if (s === 'approved') return 'approved';
  if (s === 'rejected') return 'rejected-director';
  if (s === 'rejected_by_peer') return 'rejected-peer';
  return 'neutral';
}

function boardPostStatusLabel(status) {
  const s = String(status || '').trim().toLowerCase();
  if (s === 'in_review') return 'Da approvare';
  if (s === 'approved') return 'Pronta da pubblicare';
  if (s === 'published') return 'Pubblicata';
  if (s === 'rejected') return 'Rifiutata';
  if (s === 'draft') return 'Bozza';
  return status || '-';
}

function summarizeTargets(targets, userLabelsById) {
  const arr = Array.isArray(targets) ? targets : [];
  if (!arr.length) return 'Tutti';
  if (arr.some((t) => String(t?.targetType || '').trim() === 'all')) return 'Tutti';
  const roleTargets = arr
    .filter((t) => String(t?.targetType || '').trim() === 'role')
    .map((t) => String(t?.targetValue || '').trim())
    .filter(Boolean);
  if (roleTargets.length) return `Ruoli: ${roleTargets.join(', ')}`;
  const userTargets = arr
    .filter((t) => String(t?.targetType || '').trim() === 'user')
    .map((t) => String(t?.targetValue || '').trim())
    .filter(Boolean);
  if (!userTargets.length) return 'Nessuno';
  const labels = userTargets.map((id) => userLabelsById.get(id) || id);
  if (labels.length <= 3) return labels.join(' · ');
  return `${labels.slice(0, 3).join(' · ')} +${labels.length - 3}`;
}

function renderDraftAttachmentsHtml(attachments) {
  const list = Array.isArray(attachments) ? attachments : [];
  if (!list.length) return '<div style="color:var(--text-secondary);">Nessun allegato.</div>';
  return list.map((a, idx) => `
    <div style="display:flex; gap:.5rem; align-items:center; margin-top:.35rem;">
      <span style="font-size:.83rem;">${escapeHtml(a.fileName || 'allegato')}</span>
      <button type="button" class="btn btn-outline btn-sm" data-edit-action="remove-attachment" data-idx="${idx}">Rimuovi</button>
    </div>
  `).join('');
}

export async function renderBoardModerationView(container, { onChanged } = {}) {
  container.innerHTML = '<div class="card"><div class="card-title">Gestione approvazioni</div><p style="color:var(--text-secondary);">Caricamento...</p></div>';
  try {
    const [data, shiftRequests, users] = await Promise.all([
      apiGetBoardModerationQueue(50),
      apiListShiftChangeRequests('pending'),
      apiGetBoardTargetUsers(),
    ]);
    const items = data?.items || [];
    const reqs = shiftRequests || [];
    const userLabelsById = new Map(
      (Array.isArray(users) ? users : []).map((u) => [String(u?.id || ''), targetUserLabel(u)]),
    );
    const itemById = new Map(items.map((it) => [String(it.id), it]));
    container.innerHTML = `
      <div class="card">
        <div class="card-title">Gestione approvazioni</div>
        <div style="display:flex; gap:.5rem; align-items:center; margin-bottom:.75rem;">
          <label style="font-size:.85rem; color:var(--text-secondary);">Tipo richiesta:</label>
          <select id="board-approval-type" class="input" style="max-width:260px;">
            <option value="all">Tutte</option>
            <option value="posts">Pubblicazioni bacheca</option>
            <option value="shifts">Richieste cambio turno</option>
          </select>
        </div>
        <div id="board-approval-posts" class="board-moderation-list">
          ${items.length === 0 ? '<p style="color:var(--text-secondary);">Nessuna comunicazione in coda approvazione.</p>' : items.map((it) => `
            <article class="board-item board-item--compact" data-id="${it.id}">
              <div style="display:flex; justify-content:space-between; gap:.75rem;">
                <strong>${escapeHtml(it.title)}</strong>
                <span class="badge">${escapeHtml(it.priority)}</span>
              </div>
              <div style="font-size:.85rem; color:var(--text-secondary); display:flex; gap:.4rem; align-items:center; flex-wrap:wrap;">
                <span class="badge board-status-badge board-status-badge--${escapeHtml(String(it.status || '').toLowerCase().replaceAll('_', '-'))}">${escapeHtml(boardPostStatusLabel(it.status))}</span>
                <span>categoria: ${escapeHtml(it.category)}</span>
              </div>
              <div style="font-size:.83rem; color:var(--text-secondary); margin-top:.2rem;">Data/Ora: ${escapeHtml(formatDateTimeIt(it.publishedAt || it.createdAt || ''))}</div>
              <div style="font-size:.83rem; color:var(--text-secondary); margin-top:.25rem;">Destinatari: ${escapeHtml(summarizeTargets(it.targets, userLabelsById))}</div>
              <div style="margin-top:.4rem;">${escapeHtml(String(it.body || '').slice(0, 200))}</div>
              <div style="display:flex; gap:.5rem; margin-top:.6rem;">
                <button class="btn btn-outline btn-sm" data-edit-action="toggle-editor">Modifica</button>
                <button class="btn btn-primary btn-sm" data-action="publish">Approva e pubblica</button>
                <button class="btn btn-outline btn-sm" data-action="reject">Rifiuta</button>
              </div>
              <div class="board-moderation-editor" data-editor style="display:none; margin-top:.7rem; border-top:1px solid var(--border); padding-top:.7rem;">
                <div class="form-group">
                  <label>Titolo</label>
                  <input type="text" class="input" data-edit-field="title" value="${escapeAttr(it.title || '')}" />
                </div>
                <div class="form-group">
                  <label>Contenuto</label>
                  <textarea class="input" data-edit-field="body" rows="6">${escapeHtml(it.body || '')}</textarea>
                </div>
                <div class="form-row">
                  <div class="form-group">
                    <label>Categoria</label>
                    <select class="input" data-edit-field="category">
                      <option value="comunicazioni" ${it.category === 'comunicazioni' ? 'selected' : ''}>Comunicazioni</option>
                      <option value="eventi" ${it.category === 'eventi' ? 'selected' : ''}>Eventi</option>
                      <option value="budget" ${it.category === 'budget' ? 'selected' : ''}>Budget</option>
                      <option value="organizzazione" ${it.category === 'organizzazione' ? 'selected' : ''}>Organizzazione</option>
                      <option value="formazione" ${it.category === 'formazione' ? 'selected' : ''}>Formazione</option>
                      <option value="sicurezza" ${it.category === 'sicurezza' ? 'selected' : ''}>Sicurezza</option>
                      <option value="qualita" ${it.category === 'qualita' ? 'selected' : ''}>Qualita</option>
                      <option value="protocollo" ${it.category === 'protocollo' ? 'selected' : ''}>Protocollo</option>
                      <option value="amministrazione" ${it.category === 'amministrazione' ? 'selected' : ''}>Amministrazione</option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label>Priorità</label>
                    <select class="input" data-edit-field="priority">
                      <option value="info" ${it.priority === 'info' ? 'selected' : ''}>Informazione</option>
                      <option value="important" ${it.priority === 'important' ? 'selected' : ''}>Importante</option>
                      <option value="urgent" ${it.priority === 'urgent' ? 'selected' : ''}>Urgente</option>
                    </select>
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-group">
                    <label>Data/Ora evento (opzionale)</label>
                    <input type="datetime-local" class="input" data-edit-field="eventStartAt" value="${escapeAttr(toDateTimeLocalValue(it.eventStartAt))}" />
                  </div>
                  <div class="form-group">
                    <label>Pubblicazione programmata (opzionale)</label>
                    <input type="datetime-local" class="input" data-edit-field="scheduledAt" value="${escapeAttr(toDateTimeLocalValue(it.scheduledAt))}" />
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-group">
                    <label>Scadenza (opzionale)</label>
                    <input type="datetime-local" class="input" data-edit-field="expiresAt" value="${escapeAttr(toDateTimeLocalValue(it.expiresAt))}" />
                  </div>
                  <div class="form-group">
                    <label><input type="checkbox" data-edit-field="requiresAck" ${it.requiresAck ? 'checked' : ''} /> Richiede conferma lettura</label>
                  </div>
                </div>
                <div class="form-group">
                  <label>Parametri target (JSON)</label>
                  <textarea class="input" data-edit-field="targets" rows="4">${escapeHtml(JSON.stringify(it.targets || [], null, 2))}</textarea>
                </div>
                <div class="form-group">
                  <label>Allegati</label>
                  <div data-edit-attachments>${renderDraftAttachmentsHtml(it.attachments || [])}</div>
                  <input type="file" class="input" data-edit-field="newFiles" multiple style="margin-top:.55rem;" />
                </div>
                <div style="display:flex; gap:.5rem; align-items:center;">
                  <button type="button" class="btn btn-primary btn-sm" data-edit-action="save-editor">Salva modifiche</button>
                  <button type="button" class="btn btn-secondary btn-sm" data-edit-action="cancel-editor">Chiudi</button>
                </div>
              </div>
            </article>
          `).join('')}
        </div>
        <div id="board-approval-shifts" class="board-moderation-list" style="margin-top:1rem;">
          ${reqs.length === 0 ? '<p style="color:var(--text-secondary);">Nessuna richiesta cambio turno pendente.</p>' : reqs.map((r) => `
            <article class="board-item board-item--compact" data-shift-request-id="${r.id}">
              <div style="display:flex; justify-content:space-between; gap:.75rem;">
                <strong>Scambio proposto</strong>
                <span class="badge shift-status-badge shift-status-badge--${shiftRequestStatusClass(r.status)}">${escapeHtml(shiftRequestStatusLabel(r.status))}</span>
              </div>
              <div style="font-size:.85rem; color:var(--text-secondary);">
                ${escapeHtml(r.requester_username || r.requester_user_id)} → ${escapeHtml(r.requested_username || r.requested_user_id)}
              </div>
              <div style="margin-top:.45rem; display:flex; flex-wrap:wrap; gap:.35rem; align-items:center;">
                <span style="font-size:.82rem; color:var(--text-secondary);">Prima:</span>
                <span class="badge shift-person-badge">${escapeHtml(personBadgeLabel(r.requester_username || r.requester_user_id, r.requester_sigla))}</span>
                ${shiftBadgeHtml({ name: r.slot_name, category: r.slot_category, timeSlot: r.slot_time_slot, date: r.slot_date })}
                <span class="badge shift-person-badge">${escapeHtml(personBadgeLabel(r.requested_username || r.requested_user_id, r.requested_sigla))}</span>
                ${shiftBadgeHtml({ name: r.target_slot_name, category: r.target_slot_category, timeSlot: r.target_slot_time_slot, date: r.target_slot_date })}
              </div>
              <div style="margin-top:.35rem; display:flex; flex-wrap:wrap; gap:.35rem; align-items:center;">
                <span style="font-size:.82rem; color:var(--text-secondary);">Ora:</span>
                <span class="badge shift-person-badge">${escapeHtml(personBadgeLabel(r.requested_username || r.requested_user_id, r.requested_sigla))}</span>
                ${shiftBadgeHtml({ name: r.slot_name, category: r.slot_category, timeSlot: r.slot_time_slot, date: r.slot_date })}
                <span class="badge shift-person-badge">${escapeHtml(personBadgeLabel(r.requester_username || r.requester_user_id, r.requester_sigla))}</span>
                ${shiftBadgeHtml({ name: r.target_slot_name, category: r.target_slot_category, timeSlot: r.target_slot_time_slot, date: r.target_slot_date })}
              </div>
              <div style="margin-top:.35rem;">Motivazione: ${escapeHtml(String(r.reason || '-').slice(0, 240))}</div>
              <div style="display:flex; gap:.5rem; margin-top:.6rem;">
                <button class="btn btn-primary btn-sm" data-shift-action="approve">Approva e applica sul piano</button>
                <button class="btn btn-outline btn-sm" data-shift-action="reject">Rifiuta</button>
              </div>
            </article>
          `).join('')}
        </div>
      </div>
    `;
    const typeSel = container.querySelector('#board-approval-type');
    const postsWrap = container.querySelector('#board-approval-posts');
    const shiftsWrap = container.querySelector('#board-approval-shifts');
    const syncTypeVisibility = () => {
      const mode = typeSel?.value || 'all';
      if (postsWrap) postsWrap.style.display = (mode === 'all' || mode === 'posts') ? '' : 'none';
      if (shiftsWrap) shiftsWrap.style.display = (mode === 'all' || mode === 'shifts') ? '' : 'none';
    };
    typeSel?.addEventListener('change', syncTypeVisibility);
    syncTypeVisibility();

    container.querySelectorAll('[data-id]').forEach((article) => {
      const postId = String(article.dataset.id || '');
      const item = itemById.get(postId);
      if (!item) return;
      const editorEl = article.querySelector('[data-editor]');
      const attachmentsWrap = article.querySelector('[data-edit-attachments]');
      const filesInputEl = article.querySelector('[data-edit-field="newFiles"]');
      let draftAttachments = [...(item.attachments || [])];

      const refreshAttachmentsView = () => {
        if (!attachmentsWrap) return;
        attachmentsWrap.innerHTML = renderDraftAttachmentsHtml(draftAttachments);
        attachmentsWrap.querySelectorAll('[data-edit-action="remove-attachment"]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const idx = Number(btn.dataset.idx || -1);
            if (idx < 0 || idx >= draftAttachments.length) return;
            draftAttachments.splice(idx, 1);
            refreshAttachmentsView();
          });
        });
      };
      refreshAttachmentsView();

      article.querySelector('[data-edit-action="toggle-editor"]')?.addEventListener('click', () => {
        if (!editorEl) return;
        editorEl.style.display = editorEl.style.display === 'none' ? '' : 'none';
      });
      article.querySelector('[data-edit-action="cancel-editor"]')?.addEventListener('click', () => {
        if (!editorEl) return;
        editorEl.style.display = 'none';
      });
      article.querySelector('[data-edit-action="save-editor"]')?.addEventListener('click', async () => {
        try {
          let targets = [];
          const targetsRaw = article.querySelector('[data-edit-field="targets"]')?.value || '[]';
          try {
            const parsed = JSON.parse(targetsRaw);
            targets = Array.isArray(parsed) ? parsed : [];
          } catch (_) {
            throw new Error('JSON target non valido');
          }
          const newFiles = Array.from(filesInputEl?.files || []);
          if (newFiles.length) {
            for (const file of newFiles) {
              const up = await apiUploadBoardAttachment(file);
              draftAttachments.push({
                fileName: up.fileName,
                url: up.url,
                mimeType: up.mimeType,
                sizeBytes: up.sizeBytes,
              });
            }
          }
          const payload = {
            title: article.querySelector('[data-edit-field="title"]')?.value?.trim() || '',
            body: article.querySelector('[data-edit-field="body"]')?.value?.trim() || '',
            category: article.querySelector('[data-edit-field="category"]')?.value || 'comunicazioni',
            priority: article.querySelector('[data-edit-field="priority"]')?.value || 'info',
            eventStartAt: article.querySelector('[data-edit-field="eventStartAt"]')?.value?.trim() || null,
            eventEndAt: null,
            scheduledAt: article.querySelector('[data-edit-field="scheduledAt"]')?.value?.trim() || null,
            expiresAt: article.querySelector('[data-edit-field="expiresAt"]')?.value?.trim() || null,
            requiresAck: Boolean(article.querySelector('[data-edit-field="requiresAck"]')?.checked),
            attachments: draftAttachments,
            targets,
          };
          await apiUpdateBoardPost(postId, payload);
          if (typeof onChanged === 'function') onChanged();
          await renderBoardModerationView(container, { onChanged });
        } catch (e) {
          window.alert(e?.message || 'Errore salvataggio modifiche comunicazione.');
        }
      });
    });

    container.querySelectorAll('[data-action]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const article = btn.closest('[data-id]');
        const postId = article?.dataset?.id;
        const action = btn.dataset.action;
        if (!postId) return;
        try {
          if (action === 'publish') await apiPublishBoardPost(postId);
          if (action === 'reject') await apiRejectBoardPost(postId);
          if (typeof onChanged === 'function') onChanged();
          await renderBoardModerationView(container, { onChanged });
        } catch (e) {
          window.alert(e?.message || 'Operazione moderazione fallita.');
        }
      });
    });
    container.querySelectorAll('[data-shift-action]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const article = btn.closest('[data-shift-request-id]');
        const reqId = article?.dataset?.shiftRequestId;
        const action = btn.dataset.shiftAction;
        if (!reqId) return;
        const note = window.prompt(action === 'approve' ? 'Nota direttore (opzionale):' : 'Motivo rifiuto (opzionale):', '') || '';
        try {
          if (action === 'approve') await apiApproveShiftChangeRequest(reqId, note);
          if (action === 'reject') await apiRejectShiftChangeRequest(reqId, note);
          if (typeof onChanged === 'function') onChanged();
          await renderBoardModerationView(container, { onChanged });
        } catch (e) {
          window.alert(e?.message || 'Operazione su richiesta cambio turno fallita.');
        }
      });
    });
  } catch (e) {
    container.innerHTML = `<div class="card"><div class="card-title">Gestione approvazioni</div><p style="color:var(--danger);">${e?.message || 'Errore caricamento coda.'}</p></div>`;
  }
}
